import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import useApiToken from "../../hooks/useApiToken";
import {
  fetchScheduledResourcesByDateWindow,
  fetchTicketsByIds,
  fetchWorkRoles,
  fetchUsersByWorkRoleIds,
  fetchProjectRoles,
  fetchPermissions,
  fetchPaycorTimeOff,
  fetchSoftSchedules,
  getDate,
  getResourceName,
  getTicketId,
  getProjectIdFromTicket,
  getTicketSummary,
} from "../../api/client";

const STATUS_WHITELIST = [
  "assembly",
  "engineering",
  "Implementation",
  "procurement",
  "project closeout",
  "punchlist",
  "will call",
];

function normalizeArray(result) {
  if (Array.isArray(result)) return result;
  if (result && Array.isArray(result.items)) return result.items;
  return [];
}

// More permissive normalizer for work roles (API shape may differ)
function normalizeWorkRoles(result) {
  if (Array.isArray(result)) return result;
  if (result && Array.isArray(result.items)) return result.items;
  if (result && Array.isArray(result.value)) return result.value; // some APIs return { value: [...] }
  if (result && Array.isArray(result.data)) return result.data; // common 'data' wrapper
  if (result && typeof result === 'object') {
    const vals = Object.values(result);
    if (vals.every(v => typeof v === 'object')) {
      const guess = vals.filter(v => v && (v.name || v.id || v.inactiveFlag !== undefined));
      if (guess.length) return guess;
    }
  }
  return [];
}

const ScheduleDataContext = createContext(null);

export function ScheduleDataProvider({ children }) {
  const { token, loading: tokenLoading, error: tokenError } = useApiToken();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [ticketSummaryMap, setTicketSummaryMap] = useState(new Map());
  const [ticketProjectMap, setTicketProjectMap] = useState(new Map());
  const [ticketProjectNameMap, setTicketProjectNameMap] = useState(new Map());
  const [selectedTicketStatuses, setSelectedTicketStatuses] = useState(() => {
    try { const raw = localStorage.getItem("rm.selectedTicketStatuses"); return raw ? JSON.parse(raw) : []; } catch { return []; }
  });
  // Tracks which statuses have been auto-initialized so user unchecks are preserved across sessions
  const seenStatusesRef = useRef((() => {
    try { const raw = localStorage.getItem("rm.seenTicketStatuses"); return new Set(raw ? JSON.parse(raw) : []); } catch { return new Set(); }
  })());
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [refreshVersion, setRefreshVersion] = useState(0); // increments after each successful load
  const [softSchedules, setSoftSchedules] = useState([]);
  const loadedWindowRef = useRef({ start: null, end: null });
  const [rangeLoading, setRangeLoading] = useState(false);
  // Track the date window for which scheduled resources have been loaded
  const resourcesWindowRef = useRef({ start: null, end: null });
  // Track non-blocking background loads (e.g., ticket backfill)
  const bgLoadCountRef = useRef(0);
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  function incBg() { bgLoadCountRef.current++; setBackgroundLoading(true); }
  function decBg() { bgLoadCountRef.current = Math.max(0, bgLoadCountRef.current - 1); if (bgLoadCountRef.current === 0) setBackgroundLoading(false); }

  // --- Prefilter gating ---
  // When true, the main data load has been committed; when false, we show the prefilter screen.
  const [prefilterReady, setPrefilterReady] = useState(false);
  // Metadata loading happens before prefilter commit so the picker has data to show
  const [metadataLoaded, setMetadataLoaded] = useState(false);

  // Active date range (set by prefilter commit or inline date picker)
  const [activeDateStart, setActiveDateStart] = useState(null);
  const [activeDateEnd, setActiveDateEnd] = useState(null);
  const activeDateStartRef = useRef(null);
  const activeDateEndRef = useRef(null);

  const [filters, setFilters] = useState(() => ({ project: "", ticket: "", resource: "" }));
  const [workRoles, setWorkRoles] = useState([]);
  const [workRolesLoading, setWorkRolesLoading] = useState(false);
  const [workRolesError, setWorkRolesError] = useState(null);
  const [selectedWorkRoleIds, setSelectedWorkRoleIds] = useState(() => {
    try { const raw = localStorage.getItem("rm.selectedWorkRoleIds"); return raw ? JSON.parse(raw) : []; } catch { return []; }
  });
  const [usersForRoles, setUsersForRoles] = useState([]);
  const [projectRoles, setProjectRoles] = useState([]); // hidden list, preloaded
  const [permissions, setPermissions] = useState(null);
  const [timeOffDays, setTimeOffDays] = useState(new Set()); // Set of "employeeNumber_YYYY-MM-DD" keys
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUserNames, setSelectedUserNames] = useState(() => {
    try { const raw = localStorage.getItem("rm.selectedUserNames"); return raw ? JSON.parse(raw) : []; } catch { return []; }
  });
  const [selectedUserIds, setSelectedUserIds] = useState(() => {
    try { const raw = localStorage.getItem("rm.selectedUserIds"); return raw ? JSON.parse(raw) : []; } catch { return []; }
  });

  function unwrapId(v) {
    if (v == null) return null;
    if (typeof v === 'object') {
      return unwrapId(v.id ?? v.identifier ?? v.value ?? v.objectId ?? v.ticketId ?? v.projectId ?? null);
    }
    return v;
  }
  function extractProjectId(p) {
    return unwrapId(p?.id) ?? unwrapId(p?.projectId) ?? unwrapId(p?.project) ?? unwrapId(p?.['project/id']) ?? null;
  }
  function toIsoNoMsZ(d) { const iso = d.toISOString(); return iso.slice(0,19) + 'Z'; }
  function startOfDayUtc(d) { return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 0,0,0)); }
  function addMonths(d, months) { const dt = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 0,0,0)); dt.setUTCMonth(dt.getUTCMonth()+months); return dt; }
  function addDaysUtc(d, days) { const dt = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 0,0,0)); dt.setUTCDate(dt.getUTCDate()+days); return dt; }
  function getInitialMonthsRadius() {
    try {
      const v = Number(window.localStorage.getItem('rm.initialMonths'));
      if (Number.isFinite(v) && v >= 1 && v <= 3) return Math.floor(v);
    } catch {}
    return 1; // default to ±1 month for faster initial load
  }
  function getPageSize() {
    try {
      const v = Number(window.localStorage.getItem('rm.pageSize'));
      if (Number.isFinite(v) && v >= 50 && v <= 500) return Math.floor(v);
    } catch {}
    return 200;
  }
  function getTicketBatchSize() {
    try {
      const v = Number(window.localStorage.getItem('rm.ticketBatchSize'));
      if (Number.isFinite(v) && v >= 25 && v <= 200) return Math.floor(v);
    } catch {}
    return 100;
  }

  async function ensureDataForRange(rangeStart, rangeEnd, force = false, opts = {}) {
    try {
      if (!token) return;
      const s = startOfDayUtc(rangeStart);
      const e = startOfDayUtc(rangeEnd);
      const current = loadedWindowRef.current;
      const needLeft = force || !current.start || s < current.start;
      const needRight = force || !current.end || e > current.end;
      if (!needLeft && !needRight && !force) return;
      const newStart = current.start ? (current.start < s ? current.start : s) : s;
      const newEnd = current.end ? (current.end > e ? current.end : e) : e;
      // Determine scheduled resource fetch window: initial ±3 months around today, then expand to cover visible window
      const rw = resourcesWindowRef.current;
      let fetchStart = null;
      let fetchEnd = null;
      const needResources = force || (!rw.start || s < rw.start) || (!rw.end || e > rw.end);
      if (needResources) {
        if (!rw.start && !rw.end) {
          fetchStart = s;
          fetchEnd = e;
        } else {
          const extendLeft = !rw.start || s < rw.start;
          const extendRight = !extendLeft && (!rw.end || e > rw.end);
          if (extendLeft) {
            fetchStart = s;
            fetchEnd = addMonths(fetchStart, 1);
            // Limit extension if caller provided a tighter window
            if (e && e < fetchEnd) fetchEnd = e;
            const maxDays = Number(opts.maxDays) || null;
            if (maxDays && Number.isFinite(maxDays) && maxDays > 0) {
              const limitEnd = addDaysUtc(fetchStart, Math.min(maxDays, 90));
              if (limitEnd < fetchEnd) fetchEnd = limitEnd;
            }
          } else if (extendRight) {
            fetchStart = rw.end || s;
            fetchEnd = addMonths(fetchStart, 1);
            if (e && e < fetchEnd) fetchEnd = e;
            const maxDays = Number(opts.maxDays) || null;
            if (maxDays && Number.isFinite(maxDays) && maxDays > 0) {
              const limitEnd = addDaysUtc(fetchStart, Math.min(maxDays, 90));
              if (limitEnd < fetchEnd) fetchEnd = limitEnd;
            }
          } else {
            // Force-only refresh while within current window: re-fetch the requested visible slice
            fetchStart = s;
            fetchEnd = e;
          }
        }
        setRangeLoading(true);
        // Resolve selected user IDs to member identifier strings for API-side filtering
        const memberIdentifiers = selectedUserIds.length
          ? selectedUserIds.map(uid => {
              const user = (usersForRoles || []).find(u => {
                const id = u?.id ?? u?.identifier ?? u?.member?.identifier ?? u?.memberId ?? u?.userId;
                return id != null && String(id) === String(uid);
              });
              return user?.identifier || user?.member?.identifier || null;
            }).filter(Boolean)
          : [];
        const srWindow = normalizeArray(await fetchScheduledResourcesByDateWindow(toIsoNoMsZ(fetchStart), toIsoNoMsZ(fetchEnd), token, { pageSize: getPageSize(), memberIdentifiers, doneFlag: false }));
        if (srWindow && srWindow.length) {
          // Build/merge assignments from scheduled resources
          const newEntries = srWindow.map(sr => {
            const start = getDate(sr?.dateStart);
            let end = getDate(sr?.dateEnd);
            if (start && (!end || end <= start)) end = new Date(start.getTime() + 30 * 60 * 1000);
            if (!start) return null;
            const ticketId = unwrapId(sr?.objectId ?? sr?.ticketId ?? sr?.ticket?.id ?? sr?.ticket?.identifier ?? getTicketId(sr));
            const projectId = sr?.projectId || sr?.project?.id || null;
            const summary = null; // filled after tickets backfill
            let resourceName = getResourceName(sr);
            const ident = sr?.member?.identifier || sr?.memberIdentifier || sr?.member?.id || sr?.memberId;
            if (ident && resourceName && !resourceName.toLowerCase().includes(String(ident).toLowerCase())) {
              resourceName = `${resourceName} (${ident})`;
            }
            const resourceIdentifier = unwrapId(sr?.member?.id ?? sr?.memberId ?? sr?.member?.identifier ?? sr?.memberIdentifier ?? null);
            return { id: sr?.id, ticketId, projectId, start, end, resourceName, resourceIdentifier, ticketSummary: summary, raw: sr };
          }).filter(Boolean);
          if (newEntries.length) {
            setAssignments(prev => {
              const byId = new Map((prev || []).map(a => [a.id, a]));
              for (const a of newEntries) byId.set(a.id, a);
              return Array.from(byId.values());
            });

            // Backfill ticket details unless caller opted to skip for speed
            if (!opts.skipBackfill) {
              // opts.forceTicketRefetch bypasses the stale-closure cache: loadDateRange resets
              // tickets state via setTickets([]) but the closure still sees the old value because
              // React hasn't re-rendered yet. Passing this flag treats the cache as empty so
              // all ticket IDs from the new batch are fetched.
              const existingTicketIds = opts.forceTicketRefetch
                ? new Set()
                : new Set((tickets || []).map(t => unwrapId(t?.id ?? t?.ticketId ?? t?.objectId)).filter(Boolean));
              const missingTicketIds = Array.from(new Set(newEntries.map(ne => ne.ticketId).filter(Boolean))).filter(tid => !existingTicketIds.has(tid));
              if (missingTicketIds.length) {
                // Perform ticket backfill in background to improve perceived load times
                Promise.resolve().then(async () => {
                  incBg();
                  try {
                    const fetchedTix = normalizeArray(await fetchTicketsByIds(missingTicketIds, token, { pageSize: 200, batchSize: getTicketBatchSize() }));
                    if (fetchedTix.length) {
                    // Merge tickets
                    setTickets(prev => {
                      const map = new Map((prev || []).map(t => [unwrapId(t?.id ?? t?.ticketId ?? t?.objectId), t]));
                      for (const t of fetchedTix) {
                        const key = unwrapId(t?.id ?? t?.ticketId ?? t?.objectId) ?? Math.random();
                        map.set(key, t);
                      }
                      return Array.from(map.values());
                    });
                    // Update project/name maps
                    const tProject = new Map(ticketProjectMap);
                    const tSummary = new Map(ticketSummaryMap);
                    const tProjectName = new Map(ticketProjectNameMap);
                    fetchedTix.forEach(t => {
                      const tid = unwrapId(t?.id ?? t?.ticketId ?? t?.objectId);
                      const pid = getProjectIdFromTicket(t);
                      if (tid && pid != null) tProject.set(tid, pid);
                      const s2 = getTicketSummary(t);
                      if (tid && s2) tSummary.set(tid, s2);
                      const pname = (t?.project && (t.project.name || t.project.title || t.project.projectName)) || t?.projectName || null;
                      if (tid && pname) tProjectName.set(tid, String(pname));
                    });
                    setTicketProjectMap(tProject);
                    setTicketSummaryMap(tSummary);
                    setTicketProjectNameMap(tProjectName);
                    // Rehydrate assignments with new ticket info where missing
                    setAssignments(prev => prev.map(a => {
                      const tid = a.ticketId;
                      if (!tid) return a;
                      const summary = tSummary.get(tid) || a.ticketSummary || null;
                      const projectId = a.projectId ?? tProject.get(tid) ?? null;
                      return (summary !== a.ticketSummary || projectId !== a.projectId) ? { ...a, ticketSummary: summary, projectId } : a;
                    }));
                  }
                  } catch (e3) {
                    console.warn('Backfill tickets by IDs failed', e3);
                  } finally { decBg(); }
                });
              }
            }
          }
        }
        // Update loaded resources window
        const nextStart = rw.start ? (fetchStart < rw.start ? fetchStart : rw.start) : fetchStart;
        const nextEnd = rw.end ? (fetchEnd > rw.end ? fetchEnd : rw.end) : fetchEnd;
        resourcesWindowRef.current = { start: nextStart, end: nextEnd };
        setRangeLoading(false);
      }
      loadedWindowRef.current = { start: newStart, end: newEnd };
    } catch (e) {
      console.error('ensureDataForRange failed', e);
      // Surface to UI so failures (e.g., server down) are visible
      setError(e);
    }
  }

  async function load(force = false, dateRange = null, loadOpts = {}) {
    try {
      if (!token && !tokenError) return;
      setLoading(true);
      setError(null);
      // Load permissions first
      try {
        const perms = await fetchPermissions(token);
        setPermissions(perms || {});
      } catch (e) {
        console.warn('Failed to load permissions', e);
        setPermissions({});
      }
      const today = new Date();
      let start, end;
      if (dateRange && dateRange.start && dateRange.end) {
        start = new Date(dateRange.start);
        end = new Date(dateRange.end);
      } else {
        const m = getInitialMonthsRadius();
        start = addMonths(today, -m);
        end = addMonths(today, +m);
      }
      // Track active date range
      setActiveDateStart(start);
      setActiveDateEnd(end);
      // Fetch Paycor time-off data for the active date window
      try {
        const startStr = start.toISOString().slice(0, 10);
        const endStr = end.toISOString().slice(0, 10);
        const timeOffRecords = await fetchPaycorTimeOff(startStr, endStr, token);
        const tod = new Set();
        for (const r of (timeOffRecords || [])) {
          if (r.employeeNumber == null || !r.start_time) continue;
          tod.add(`${r.employeeNumber}_${String(r.start_time).slice(0, 10)}`);
        }
        setTimeOffDays(tod);
      } catch (e) {
        console.warn('Failed to load Paycor time-off data', e);
      }
      await ensureDataForRange(start, end, force, loadOpts);
      // Preload project roles (hidden list for modal error flow)
      try {
        const pr = await fetchProjectRoles(token);
        const arr = Array.isArray(pr) ? pr : (Array.isArray(pr?.items) ? pr.items : []);
        setProjectRoles(arr);
      } catch (e) {
        console.warn('Failed to load project roles', e);
        setProjectRoles([]);
      }
      // Load work roles once (if not already)
      if (workRoles.length === 0) {
        try {
          setWorkRolesLoading(true);
          setWorkRolesError(null);
          const rawWr = await fetchWorkRoles(token);
          const wr = normalizeWorkRoles(rawWr);
          setWorkRoles(wr);
        } catch (e) {
          console.error("Failed to load work roles", e);
          setWorkRolesError(e);
        } finally {
          setWorkRolesLoading(false);
        }
      }
      // Load soft schedules for the active date window
      try {
        const softIso = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}T00:00:00Z`;
        const softList = await fetchSoftSchedules(softIso, token);
        setSoftSchedules(softList);
      } catch (e) {
        console.warn('Failed to load soft schedules', e);
      }
      setLastRefreshed(new Date());
      setRefreshVersion(v => v + 1);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }

  // --- Metadata preload: load work roles, permissions, and project roles eagerly
  // so the prefilter screen can show user/role pickers before full data load.
  async function loadMetadata() {
    if (!token) return;
    try {
      // Load permissions
      try {
        const perms = await fetchPermissions(token);
        setPermissions(perms || {});
      } catch (e) {
        console.warn('Failed to load permissions', e);
        setPermissions({});
      }
      // Load work roles
      if (workRoles.length === 0) {
        try {
          setWorkRolesLoading(true);
          setWorkRolesError(null);
          const rawWr = await fetchWorkRoles(token);
          const wr = normalizeWorkRoles(rawWr);
          setWorkRoles(wr);
        } catch (e) {
          console.error("Failed to load work roles", e);
          setWorkRolesError(e);
        } finally {
          setWorkRolesLoading(false);
        }
      }
      // Preload project roles
      try {
        const pr = await fetchProjectRoles(token);
        const arr = Array.isArray(pr) ? pr : (Array.isArray(pr?.items) ? pr.items : []);
        setProjectRoles(arr);
      } catch (e) {
        console.warn('Failed to load project roles', e);
        setProjectRoles([]);
      }
    } catch (e) {
      console.error('loadMetadata failed', e);
    } finally {
      setMetadataLoaded(true);
      setLoading(false);
    }
  }

  // commitPrefilter: called from the PreFilter screen to start actual data load
  async function commitPrefilter(dateRange) {
    setPrefilterReady(true);
    await load(false, dateRange);
  }

  // loadDateRange: called from inline date picker to change the active window
  async function loadDateRange(start, end) {
    // Reset loaded windows so ensureDataForRange fetches fresh data for the new range
    loadedWindowRef.current = { start: null, end: null };
    resourcesWindowRef.current = { start: null, end: null };
    setAssignments([]);
    setTickets([]);
    setTicketSummaryMap(new Map());
    setTicketProjectMap(new Map());
    setTicketProjectNameMap(new Map());
    await load(true, { start, end }, { forceTicketRefetch: true });
  }

  useEffect(() => {
    if (!tokenLoading && token) {
      // Only load metadata eagerly; full data load waits for prefilter commit
      Promise.resolve().then(() => loadMetadata()).catch((e) => console.error('metadata load failed', e));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, tokenLoading, tokenError]);

  // Keep refs in sync with the latest active date range so the auto-refresh
  // interval (which has a stale closure) always uses the current values.
  useEffect(() => { activeDateStartRef.current = activeDateStart; }, [activeDateStart]);
  useEffect(() => { activeDateEndRef.current = activeDateEnd; }, [activeDateEnd]);

  const usersForRolesRef = useRef([]);
  useEffect(() => { usersForRolesRef.current = usersForRoles; }, [usersForRoles]);

  // Auto-refresh every 30 minutes — only when prefilter has been committed
  useEffect(() => {
    if (tokenLoading || !token || !prefilterReady) return;
    const interval = setInterval(() => {
      // Read the latest date range from refs to avoid a stale closure
      const start = activeDateStartRef.current;
      const end = activeDateEndRef.current;
      const dateRange = start && end ? { start, end } : null;
      load(true, dateRange).catch((e) => {
        console.error('auto-refresh failed', e);
        setError(e);
      });
    }, 30 * 60 * 1000); // 30 minutes
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, tokenLoading, prefilterReady]);

  // Persist selected roles/users across views and reloads
  useEffect(() => {
    try {
      localStorage.setItem("rm.selectedWorkRoleIds", JSON.stringify(selectedWorkRoleIds));
    } catch {}
  }, [selectedWorkRoleIds]);

  useEffect(() => {
    try {
      localStorage.setItem("rm.selectedUserNames", JSON.stringify(selectedUserNames));
    } catch {}
  }, [selectedUserNames]);

  useEffect(() => {
    try {
      localStorage.setItem("rm.selectedUserIds", JSON.stringify(selectedUserIds));
    } catch {}
  }, [selectedUserIds]);

  useEffect(() => {
    try { localStorage.setItem("rm.selectedTicketStatuses", JSON.stringify(selectedTicketStatuses)); } catch {}
  }, [selectedTicketStatuses]);

  const ticketStatusMap = useMemo(() => {
    const map = new Map();
    (tickets || []).forEach(t => {
      const tid = unwrapId(t?.id ?? t?.ticketId ?? t?.objectId);
      const statusName = t?.status?.name
        || t?.status?.description
        || (typeof t?.status === 'string' ? t.status : null)
        || t?.statusName
        || t?.ticketStatus?.name
        || (typeof t?.ticketStatus === 'string' ? t.ticketStatus : null)
        || null;
      if (tid != null && statusName) {
        map.set(tid, statusName);
        map.set(String(tid), statusName);
      }
    });
    return map;
  }, [tickets]);

  const allTicketStatuses = useMemo(() => {
    const names = new Set();
    (tickets || []).forEach(t => {
      const s = t?.status?.name
        || t?.status?.description
        || (typeof t?.status === 'string' ? t.status : null)
        || t?.statusName
        || t?.ticketStatus?.name
        || (typeof t?.ticketStatus === 'string' ? t.ticketStatus : null)
        || null;
      if (s) names.add(s);
    });
    return Array.from(names).filter(Boolean).sort((a, b) => a.localeCompare(b));
  }, [tickets]);

  // Auto-select newly discovered ticket statuses (ones not yet seen before)
  useEffect(() => {
    if (!allTicketStatuses || allTicketStatuses.length === 0) return;
    const seen = seenStatusesRef.current;
    const newStatuses = allTicketStatuses.filter(s => !seen.has(s));
    if (newStatuses.length === 0) return;
    newStatuses.forEach(s => seen.add(s));
    try { localStorage.setItem("rm.seenTicketStatuses", JSON.stringify(Array.from(seen))); } catch {}
    setSelectedTicketStatuses(prev => {
      const prevSet = new Set(prev);
      const toAdd = newStatuses.filter(s => !prevSet.has(s));
      return toAdd.length > 0 ? [...prev, ...toAdd] : prev;
    });
  }, [allTicketStatuses]);

  function parseProjectFilter(value) {
    if (!value) return null;
    // If contains comma, treat as set of normalized tokens
    const parts = value.split(',').map(s => s.trim()).filter(Boolean);
    if (parts.length > 1) return new Set(parts);
    return value.trim();
  }

  const filteredAssignments = useMemo(() => {
    if (!assignments || assignments.length === 0) return assignments;
    // Build set of ticket IDs that were successfully fetched from cw_project_tickets
    const validTicketIds = new Set(
      (tickets || []).map(t => unwrapId(t?.id ?? t?.ticketId ?? t?.objectId)).filter(Boolean).map(String)
    );
    const pj = parseProjectFilter(filters.project);
    const ticket = (filters.ticket || '').toLowerCase();
    const resource = (filters.resource || '').toLowerCase();
    const userIdSet = selectedUserIds.length ? new Set(selectedUserIds.map(v => String(v))) : null; // user IDs from Users API
    return assignments.filter(a => {
      // Only show assignments whose ticket was returned by cw_project_tickets
      const tidStr = a.ticketId != null ? String(a.ticketId) : '';
      if (!tidStr || !validTicketIds.has(tidStr)) return false;
      // project filtering
      if (pj) {
        const pidStr = a.projectId != null ? String(a.projectId) : '';
        if (pj instanceof Set) {
          if (!pj.has(pidStr)) return false;
        } else {
          if (!pidStr.includes(pj)) return false;
        }
      }
      // ticket filtering
      if (ticket) {
        const tidStr = a.ticketId != null ? String(a.ticketId) : '';
        if (!tidStr.toLowerCase().includes(ticket)) return false;
      }
      // resource filtering
      if (resource) {
        const rname = (a.resourceName || '').toLowerCase();
        if (!rname.includes(resource)) return false;
      }
      // user selection filtering by identifier
      if (userIdSet) {
        const rid = a.resourceIdentifier != null ? String(a.resourceIdentifier) : '';
        if (!userIdSet.has(rid)) return false;
      }
      // ticket status filtering
      if (selectedTicketStatuses.length > 0) {
        if (a.ticketId == null) return false;
        const assignTidStr = String(a.ticketId);
        const tkt = (tickets || []).find(t => {
          const tid = unwrapId(t?.id ?? t?.ticketId ?? t?.objectId);
          return tid != null && String(tid) === assignTidStr;
        });
        const statusName = tkt?.status?.name
          || tkt?.status?.description
          || (typeof tkt?.status === 'string' ? tkt.status : null)
          || tkt?.statusName
          || tkt?.ticketStatus?.name
          || (typeof tkt?.ticketStatus === 'string' ? tkt.ticketStatus : null)
          || null;
        if (!statusName || !selectedTicketStatuses.includes(statusName)) return false;
      }
      return true;
    });
  }, [assignments, tickets, filters, selectedUserIds, selectedTicketStatuses, ticketStatusMap]);

  // Same as filteredAssignments but WITHOUT the user-selection filter.
  // Used for pinned resource rows so they always show their items regardless of
  // which users are currently selected in the filter panel.
  const filteredAssignmentsForPinned = useMemo(() => {
    if (!assignments || assignments.length === 0) return assignments;
    const validTicketIds = new Set(
      (tickets || []).map(t => unwrapId(t?.id ?? t?.ticketId ?? t?.objectId)).filter(Boolean).map(String)
    );
    const pj = parseProjectFilter(filters.project);
    const ticket = (filters.ticket || '').toLowerCase();
    const resource = (filters.resource || '').toLowerCase();
    return assignments.filter(a => {
      const tidStr = a.ticketId != null ? String(a.ticketId) : '';
      if (!tidStr || !validTicketIds.has(tidStr)) return false;
      if (pj) {
        const pidStr = a.projectId != null ? String(a.projectId) : '';
        if (pj instanceof Set) { if (!pj.has(pidStr)) return false; }
        else { if (!pidStr.includes(pj)) return false; }
      }
      if (ticket) {
        const tidStr2 = a.ticketId != null ? String(a.ticketId) : '';
        if (!tidStr2.toLowerCase().includes(ticket)) return false;
      }
      if (resource) {
        const rname = (a.resourceName || '').toLowerCase();
        if (!rname.includes(resource)) return false;
      }
      // NOTE: user-selection filter intentionally omitted for pinned rows
      if (selectedTicketStatuses.length > 0) {
        if (a.ticketId == null) return false;
        const assignTidStr = String(a.ticketId);
        const tkt = (tickets || []).find(t => {
          const tid = unwrapId(t?.id ?? t?.ticketId ?? t?.objectId);
          return tid != null && String(tid) === assignTidStr;
        });
        const statusName = tkt?.status?.name
          || tkt?.status?.description
          || (typeof tkt?.status === 'string' ? tkt.status : null)
          || tkt?.statusName
          || tkt?.ticketStatus?.name
          || (typeof tkt?.ticketStatus === 'string' ? tkt.ticketStatus : null)
          || null;
        if (!statusName || !selectedTicketStatuses.includes(statusName)) return false;
      }
      return true;
    });
  }, [assignments, tickets, filters, selectedTicketStatuses, ticketStatusMap]);

  // When roles are actively cleared by the user, also clear user selections.
  // Use a ref to track previous value so we only clear when transitioning from non-empty to empty.
  const prevRoleIdsRef = useRef(selectedWorkRoleIds);
  useEffect(() => {
    const wasNonEmpty = prevRoleIdsRef.current && prevRoleIdsRef.current.length > 0;
    prevRoleIdsRef.current = selectedWorkRoleIds;
    if (wasNonEmpty && selectedWorkRoleIds.length === 0) {
      if (selectedUserIds.length) setSelectedUserIds([]);
      if (selectedUserNames.length) setSelectedUserNames([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWorkRoleIds]);

  // Fetch users when selected work role ids change (already debounced via UI staging)
  useEffect(() => {
    let cancelled = false;
    async function loadUsers() {
      if (!token) { setUsersForRoles([]); return; }
      try {
        setUsersLoading(true);
        // If no roles selected, fetch all active users; else fetch by selected roles
        const roleIds = Array.isArray(selectedWorkRoleIds) ? selectedWorkRoleIds : [];
        const users = normalizeArray(await fetchUsersByWorkRoleIds(roleIds, token));
        if (!cancelled) setUsersForRoles(users);
      } catch (e) {
        console.error("Failed to load users for roles", e);
        if (!cancelled) setUsersForRoles([]);
      } finally {
        if (!cancelled) setUsersLoading(false);
      }
    }
    loadUsers();
    return () => { cancelled = true; };
  }, [selectedWorkRoleIds, token]);

  function refresh() {
    const dateRange = activeDateStart && activeDateEnd ? { start: activeDateStart, end: activeDateEnd } : null;
    load(true, dateRange);
  }

  const value = useMemo(() => ({
    token,
    tokenLoading,
    tokenError,
    loading,
    error,
    rangeLoading,
    backgroundLoading,
    lastRefreshed,
    refreshVersion,
    assignments,
    setAssignments,
    projects,
    tickets,
    setTickets,
    filteredAssignments,
    filteredAssignmentsForPinned,
    filters,
    setFilters,
    // Permissions
    permissions,
    canWrite: permissions?.resourceManagementReadWrite === true,
    timeOffDays,
    workRoles,
    workRolesLoading,
    selectedWorkRoleIds,
    setSelectedWorkRoleIds,
    usersForRoles,
    usersLoading,
    projectRoles,
    selectedUserNames,
    setSelectedUserNames,
    selectedUserIds,
    setSelectedUserIds,
    workRolesError,
    ticketSummaryMap,
    ticketProjectMap,
    ticketProjectNameMap,
    ticketStatusMap,
    allTicketStatuses,
    selectedTicketStatuses,
    setSelectedTicketStatuses,
    refresh,
    ensureDataForRange,
    loadDateRange,
    activeDateStart,
    activeDateEnd,
    // Prefilter gating
    prefilterReady,
    metadataLoaded,
    commitPrefilter,
    // Soft schedules
    softSchedules,
    setSoftSchedules,
  }), [token, tokenLoading, tokenError, loading, error, rangeLoading, backgroundLoading, lastRefreshed, refreshVersion, assignments, projects, tickets, filteredAssignments, filteredAssignmentsForPinned, filters, workRoles, workRolesLoading, workRolesError, selectedWorkRoleIds, usersForRoles, usersLoading, selectedUserNames, selectedUserIds, ticketSummaryMap, ticketProjectMap, ticketProjectNameMap, ticketStatusMap, allTicketStatuses, selectedTicketStatuses, projectRoles, permissions, timeOffDays, prefilterReady, metadataLoaded, activeDateStart, activeDateEnd, softSchedules]);

  return (
    <ScheduleDataContext.Provider value={value}>{children}</ScheduleDataContext.Provider>
  );
}

export function useScheduleData() {
  const ctx = useContext(ScheduleDataContext);
  if (!ctx) throw new Error("useScheduleData must be used within ScheduleDataProvider");
  return ctx;
}
